// Endpoint types are strictly for physical IT/OT devices (not cloud resources)
export type EndpointType = 
  // IT Infrastructure
  | 'Switch' | 'Router' | 'Firewall' | 'VPN' | 'AP' | 'Server' | 'VM' | 'NAS/SAN' | 'AD/IdP' | 'DB' | 'API' | 'Web' | 'Backup'
  // OT/ICS Devices
  | 'Sensor' | 'Actuator' | 'PLC' | 'Remote I/O' | 'Drive/VFD' | 'HMI' | 'SCADA' | 'Historian' | 'OPC UA Server' | 'Engineering Station' | 'Patch/AV'
  // DMZ Components
  | 'Proxy' | 'Database Mirror'
  // Physical Infrastructure
  | 'UPS' | 'Patch Panel';

export type LifecycleState = 'prod' | 'test' | 'dev' | 'retired';

export interface Endpoint {
  id: string;
  org_id: string;
  system_id?: string;
  name: string;
  type?: string;
  category?: string;
  asset_type?: EndpointType;
  criticality?: string;
  vendor?: string;
  model?: string;
  firmware?: string;
  ip?: string;
  mac?: string;
  serial?: string;
  os?: string;
  backup?: boolean;
  last_patch?: string;
  notes?: string;
  vulnerabilities?: string[];
  eol_date?: string;
  compliance_tags?: string[];
  position_x?: number;
  position_y?: number;
  site_id?: string;
  metadata?: Record<string, unknown>;
  // Network configuration
  vlan_id?: number;
  default_gateway?: string;
  subnet_mask?: string;
  owner_id?: string;
  created_at: string;
  updated_at: string;
}

export type EndpointInsert = Omit<Endpoint, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
};

export type EndpointUpdate = Partial<
  Omit<Endpoint, 'id' | 'org_id' | 'created_at' | 'updated_at'>
>;

// Endpoint categories for physical IT/OT devices only
// Cloud resources are managed separately via cloud_resources table
export const ENDPOINT_CATEGORIES = {
  'Network Infrastructure': ['Switch', 'Router', 'Firewall', 'VPN', 'AP', 'Proxy'],
  'Servers & Compute': ['Server', 'VM', 'Web', 'API', 'DB', 'AD/IdP'],
  'Storage Appliances': ['NAS/SAN', 'Backup'],
  'Industrial Controllers': ['PLC', 'Remote I/O', 'Drive/VFD'],
  'HMI & Supervision': ['HMI', 'SCADA', 'Historian', 'OPC UA Server', 'Engineering Station'],
  'Field Devices': ['Sensor', 'Actuator', 'Patch/AV'],
  'Infrastructure': ['UPS', 'Patch Panel', 'Database Mirror']
} as const;

export type EndpointCategory = keyof typeof ENDPOINT_CATEGORIES;

// Domain-to-category mapping for inheritance
export const IT_CATEGORIES = ['Network Infrastructure', 'Servers & Compute', 'Storage Appliances'];
export const OT_CATEGORIES = ['Industrial Controllers', 'HMI & Supervision', 'Field Devices'];
export const SHARED_CATEGORIES = ['Infrastructure']; // Common to both IT and OT
